<template>
  <div class="resume-detail-container" :style="cssVars">
    <div class="page-header detail-header">
      <div>
        <h2>简历详情</h2>
        <p>查看候选人基本资料、求职意向和项目经历</p>
      </div>
      <el-button :icon="ArrowLeft" @click="goBack">返回列表</el-button>
    </div>

    <el-card class="box-card">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-wrapper">
        <el-skeleton :rows="10" animated />
      </div>

      <!-- 简历内容 -->
      <div v-else class="resume-content">
        <template v-if="resume">
          <!-- 概览区 -->
          <section class="overview-section">
            <div class="avatar-block">
              <el-image
                v-if="resume?.baseInfo?.avatar"
                :src="cleanAvatar(resume.baseInfo.avatar)"
                fit="cover"
                class="detail-avatar"
                :preview-src-list="[cleanAvatar(resume.baseInfo.avatar)]"
                :initial-index="0"
                preview-teleported
              />
            </div>
            <div class="overview-info">
              <div class="name-line">
                <span class="name">{{
                  resume.baseInfo?.name || '未命名'
                }}</span>
                <el-tag type="primary" effect="dark" class="intention-tag">
                  {{ resume.jobInfo?.jobIntention || '求职意向未填写' }}
                </el-tag>
              </div>
              <div class="chips">
                <el-tag>{{ resume.baseInfo?.gender || '未知性别' }}</el-tag>
                <el-tag>{{ resume.baseInfo?.birthday || '未知生日' }}</el-tag>
                <el-tag>{{
                  resume.baseInfo?.workHours
                    ? `${resume.baseInfo.workHours}年经验`
                    : '经验未填写'
                }}</el-tag>
                <el-tag>{{
                  resume.jobInfo?.cityIntention || '城市未填写'
                }}</el-tag>
                <el-tag>{{ resume.jobInfo?.salary || '薪资未填写' }}</el-tag>
                <el-tag>{{
                  resume.jobInfo?.entryTime || '入职时间未填写'
                }}</el-tag>
              </div>
              <div class="contacts">
                <el-tag type="success" class="contact-chip"
                  >电话：{{ resume.baseInfo?.phone || '未填写' }}</el-tag
                >
                <el-tag type="info" class="contact-chip"
                  >邮箱：{{ resume.baseInfo?.email || '未填写' }}</el-tag
                >
                <el-tag type="warning" class="contact-chip"
                  >政治面貌：{{
                    resume.baseInfo?.politicalStatus || '未填写'
                  }}</el-tag
                >
              </div>
            </div>
          </section>

          <!-- 基本信息 -->
          <section v-if="isShow('base')" class="section">
            <div class="section-title">
              <span>基本信息</span>
            </div>
            <el-descriptions :column="3" border class="desc-block">
              <el-descriptions-item label="姓名">{{
                resume.baseInfo?.name
              }}</el-descriptions-item>
              <el-descriptions-item label="性别">{{
                resume.baseInfo?.gender
              }}</el-descriptions-item>
              <el-descriptions-item label="生日">{{
                resume.baseInfo?.birthday
              }}</el-descriptions-item>
              <el-descriptions-item label="工作年限"
                >{{ resume.baseInfo?.workHours }} 年</el-descriptions-item
              >
              <el-descriptions-item label="电话">{{
                resume.baseInfo?.phone
              }}</el-descriptions-item>
              <el-descriptions-item label="邮箱">{{
                resume.baseInfo?.email
              }}</el-descriptions-item>
            </el-descriptions>
          </section>

          <!-- 求职意向 -->
          <section v-if="isShow('job')" class="section">
            <div class="section-title">
              <span>求职意向</span>
            </div>
            <el-descriptions :column="3" border class="desc-block">
              <el-descriptions-item label="职位意向">{{
                resume.jobInfo?.jobIntention
              }}</el-descriptions-item>
              <el-descriptions-item label="城市意向">{{
                resume.jobInfo?.cityIntention
              }}</el-descriptions-item>
              <el-descriptions-item label="期望薪资">{{
                resume.jobInfo?.salary
              }}</el-descriptions-item>
              <el-descriptions-item label="入职时间">{{
                resume.jobInfo?.entryTime
              }}</el-descriptions-item>
            </el-descriptions>
          </section>

          <!-- 技能特长 -->
          <section v-if="resume.skill" class="section">
            <div class="section-title">
              <span>技能特长</span>
            </div>
            <div class="rich-text" v-html="resume.skill" />
          </section>

          <!-- 证书荣誉 -->
          <section v-if="resume.certificate" class="section">
            <div class="section-title">
              <span>证书荣誉</span>
            </div>
            <div class="rich-text" v-html="resume.certificate" />
          </section>

          <!-- 自我评价 -->
          <section v-if="resume.selfEvaluation" class="section">
            <div class="section-title">
              <span>自我评价</span>
            </div>
            <div class="rich-text" v-html="resume.selfEvaluation" />
          </section>

          <!-- 教育背景 -->
          <section
            v-if="
              isShow('education') &&
              Array.isArray(resume.education) &&
              resume.education.length
            "
            class="section"
          >
            <div class="section-title">
              <span>教育背景</span>
            </div>
            <el-timeline class="timeline">
              <el-timeline-item
                v-for="(edu, idx) in resume.education"
                :key="`edu-${idx}`"
                :timestamp="formatRange(edu)"
                placement="top"
              >
                <el-card class="timeline-card">
                  <div class="item-title">
                    {{ edu.title }} · {{ edu.desc }} · {{ edu.degree }}
                  </div>
                  <div
                    class="item-content"
                    v-if="edu.content"
                    v-html="edu.content"
                  />
                </el-card>
              </el-timeline-item>
            </el-timeline>
          </section>

          <!-- 工作经验 -->
          <section
            v-if="
              isShow('work') && Array.isArray(resume.work) && resume.work.length
            "
            class="section"
          >
            <div class="section-title">
              <span>工作经验</span>
            </div>
            <el-timeline class="timeline">
              <el-timeline-item
                v-for="(job, idx) in resume.work"
                :key="`work-${idx}`"
                :timestamp="formatRange(job)"
                placement="top"
                type="primary"
              >
                <el-card class="timeline-card">
                  <div class="item-title">{{ job.title }} · {{ job.desc }}</div>
                  <div class="item-sub">{{ job.degree }}</div>
                  <div
                    class="item-content"
                    v-if="job.content"
                    v-html="job.content"
                  />
                </el-card>
              </el-timeline-item>
            </el-timeline>
          </section>

          <!-- 校园经历 -->
          <section
            v-if="
              isShow('campus') &&
              Array.isArray(resume.campus) &&
              resume.campus.length
            "
            class="section"
          >
            <div class="section-title">
              <span>校园经历</span>
            </div>
            <el-timeline class="timeline">
              <el-timeline-item
                v-for="(c, idx) in resume.campus"
                :key="`campus-${idx}`"
                :timestamp="formatRange(c)"
                placement="top"
                type="success"
              >
                <el-card class="timeline-card">
                  <div class="item-title">{{ c.title }} · {{ c.desc }}</div>
                  <div class="item-sub">{{ c.degree }}</div>
                  <div
                    class="item-content"
                    v-if="c.content"
                    v-html="c.content"
                  />
                </el-card>
              </el-timeline-item>
            </el-timeline>
          </section>

          <!-- 项目经历 -->
          <section
            v-if="Array.isArray(resume.project) && resume.project.length"
            class="section"
          >
            <div class="section-title">
              <span>项目经历</span>
            </div>
            <el-timeline class="timeline">
              <el-timeline-item
                v-for="(p, idx) in resume.project"
                :key="`project-${idx}`"
                :timestamp="formatRange(p)"
                placement="top"
                type="warning"
              >
                <el-card class="timeline-card">
                  <div class="item-title">{{ p.title }} · {{ p.desc }}</div>
                  <div class="item-sub">{{ p.degree }}</div>
                  <div
                    class="item-content"
                    v-if="p.content"
                    v-html="p.content"
                  />
                </el-card>
              </el-timeline-item>
            </el-timeline>
          </section>

          <!-- 实习经历 -->
          <section
            v-if="Array.isArray(resume.internship) && resume.internship.length"
            class="section"
          >
            <div class="section-title">
              <span>实习经历</span>
            </div>
            <el-timeline class="timeline">
              <el-timeline-item
                v-for="(i, idx) in resume.internship"
                :key="`intern-${idx}`"
                :timestamp="formatRange(i)"
                placement="top"
                type="info"
              >
                <el-card class="timeline-card">
                  <div class="item-title">{{ i.title }} · {{ i.desc }}</div>
                  <div class="item-sub">{{ i.degree }}</div>
                  <div
                    class="item-content"
                    v-if="i.content"
                    v-html="i.content"
                  />
                </el-card>
              </el-timeline-item>
            </el-timeline>
          </section>
        </template>
        <el-empty v-else description="暂无简历数据" />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getResumeDetail } from '@/api/resume'
import { ElMessage } from 'element-plus'
import { ArrowLeft } from '@element-plus/icons-vue'

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const resumeDetail = ref(null)

// 从接口数据中提取 resumeData
const resume = computed(() => resumeDetail.value?.resumeData || null)

// 主题与排版变量
const cssVars = computed(() => {
  const theme = resume.value?.themeColor || '#6366f1'
  const fontSize = resume.value?.fontSize
    ? `${resume.value.fontSize}px`
    : '14px'
  const moduleSpacing = resume.value?.moduleSpacing
    ? `${resume.value.moduleSpacing}px`
    : '16px'
  const lineSpacing = resume.value?.lineSpacing
    ? `${resume.value.lineSpacing}px`
    : '8px'
  return {
    '--theme-color': theme,
    '--font-size': fontSize,
    '--module-spacing': moduleSpacing,
    '--line-spacing': lineSpacing
  }
})

// 模块显示控制
const isShow = (mKey) => {
  const items = resume.value?.itemData || []
  const map = {}
  items.forEach((it) => {
    map[it.mKey] = it?.props?.isShow !== false
  })
  return map[mKey] !== false
}

// 日期范围格式化
const formatRange = (item) => {
  if (!item) return ''
  const start = item.startDate || ''
  const end = item.isNow ? '至今' : item.endDate || ''
  return `${start} ~ ${end}`
}

// 清理 avatar 文本中的反引号或多余空格
const cleanAvatar = (url) => {
  if (!url) return ''
  return String(url).replace(/`/g, '').trim()
}

/**
 * 获取简历详情数据
 */
const getResumeDetailData = async () => {
  const resumeId = route.query.resumeId
  if (!resumeId) {
    ElMessage.error('简历ID不存在')
    loading.value = false
    return
  }

  try {
    loading.value = true
    const res = await getResumeDetail(resumeId)
    resumeDetail.value = res
    console.log('resumeDetail.value', res)
  } catch (error) {
    ElMessage.error('获取简历详情失败')
  } finally {
    loading.value = false
  }
}

/**
 * 返回列表页
 */
const goBack = () => {
  router.push('/resume-manage')
}

// 初始化加载数据
onMounted(() => {
  getResumeDetailData()
})
</script>

<style scoped lang="scss">
.resume-detail-container {
  min-height: 100%;

  .detail-header {
    align-items: center;
  }

  .box-card {
    overflow: hidden;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8fafc;
      padding: 16px 20px;
      margin: -20px -20px 20px;

      .page-title {
        font-size: 20px;
        font-weight: 600;
        color: #ffffff;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        letter-spacing: 1px;
      }
    }

    .loading-wrapper {
      padding: 20px;
    }

    .resume-content {
      font-size: var(--font-size);
      line-height: calc(1em + var(--line-spacing));

      .overview-section {
        display: flex;
        gap: 20px;
        align-items: center;
        margin-bottom: var(--module-spacing);

        .detail-avatar {
          width: 120px;
          height: 120px;
          border-radius: 16px;
          object-fit: cover;
          border: 1px solid var(--admin-color-border-soft);
          box-shadow: none;
        }

        .detail-avatar:hover {
          transform: none;
          box-shadow: none;
        }

        .overview-info {
          flex: 1;

          .name-line {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 12px;

            .name {
              font-size: 24px;
              font-weight: 700;
              color: var(--admin-color-text);
            }

            .intention-tag {
              background-color: var(--theme-color);
              border-color: var(--theme-color);
            }
          }

          .chips {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            margin-bottom: 8px;
          }

          .contacts {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;

            .contact-chip {
              font-weight: 500;
            }
          }
        }
      }

      .section {
        margin-top: var(--module-spacing);

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #333;
          color: var(--admin-color-text);
          margin-bottom: 12px;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            left: 0;
            bottom: -6px;
            width: 40px;
            height: 2px;
            background: var(--admin-color-primary);
            border-radius: 999px;
          }
        }

        .desc-block {
          border-radius: 8px;
          overflow: hidden;
        }

        .timeline {
          .timeline-card {
            border-radius: 10px;
            box-shadow: none;
            border: 1px solid var(--admin-color-border-soft);

            .item-title {
              font-weight: 600;
              margin-bottom: 4px;
            }

            .item-sub {
              color: #666;
              margin-bottom: 4px;
            }

            .item-content {
              :deep(p) {
                margin: 4px 0;
              }
              :deep(ul) {
                padding-left: 18px;
              }
            }
          }
        }
      }
    }
  }
}

:deep(.el-card__header) {
  border-bottom: 1px solid var(--admin-color-border-soft);
}

/* 预览大图去圆角，避免头像放大仍然是圆形 */
:deep(.el-image-viewer__wrapper) {
  border-radius: 0 !important;
  max-width: 80vw;
  max-height: 80vh;
}

/* 富文本块通用样式 */
.rich-text {
  background-color: #f8fafc;
  padding: 14px;
  border-radius: 8px;
  border: 1px solid var(--admin-color-border-soft);
  box-shadow: none;

  :deep(p) {
    margin: 8px 0;
  }
  :deep(ul) {
    padding-left: 20px;
    margin: 6px 0;
  }
  :deep(li) {
    margin: 4px 0;
  }
}

@media (max-width: 768px) {
  .resume-detail-container {
    .box-card .resume-content .overview-section {
      align-items: flex-start;
      flex-direction: column;
    }

    :deep(.el-descriptions__body .el-descriptions__table) {
      display: block;
    }
  }
}
</style>
